import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  MenuItem,
  Breadcrumbs,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { equipmentAPI } from '../../services/api';
import { Equipment, UserRole } from '../../types';
import AdminAccessControl from '../common/AdminAccessControl';

const EquipmentManagementPage: React.FC = () => {
  const { user } = useAuth();

  // State for equipment
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>(''); // '', 'true', 'false'

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quantity: 1,
    active: true,
    costAmount: 0,
    costUnit: 30, // Default 30 minutes
    maxPerBooking: 1
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const response = await equipmentAPI.getAll(); // Get all equipment (active and inactive)
      setEquipment(response.data);
    } catch (error: any) {
      console.error('Error fetching equipment:', error);
      setError('Failed to fetch equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEquipment = () => {
    setIsEditing(false);
    setError(null);
    setFormData({
      name: '',
      description: '',
      quantity: 1,
      active: true,
      costAmount: 0,
      costUnit: 30,
      maxPerBooking: 1
    });
    setOpenCreateDialog(true);
  };

  const handleEditEquipment = (equipment: Equipment) => {
    setIsEditing(true);
    setSelectedEquipment(equipment);
    setError(null);
    setFormData({
      name: equipment.name,
      description: equipment.description || '',
      quantity: equipment.quantity,
      active: equipment.active,
      costAmount: equipment.costAmount,
      costUnit: equipment.costUnit,
      maxPerBooking: equipment.maxPerBooking
    });
    setOpenCreateDialog(true);
  };

  const handleDeleteEquipment = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteEquipment = async () => {
    if (!selectedEquipment) return;

    try {
      await equipmentAPI.delete(selectedEquipment.id);
      await fetchEquipment();
      setOpenDeleteDialog(false);
      setSelectedEquipment(null);
      setSuccessMessage('Equipment deleted successfully');
    } catch (error: any) {
      console.error('Error deleting equipment:', error);
      setError('Failed to delete equipment');
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Equipment name is required');
      return;
    }

    if (formData.quantity < 1) {
      setError('Quantity must be at least 1');
      return;
    }

    if (formData.costAmount < 0) {
      setError('Cost amount cannot be negative');
      return;
    }

    if (formData.costUnit <= 0) {
      setError('Cost unit (duration) must be greater than 0');
      return;
    }

    if (formData.maxPerBooking < 1) {
      setError('Max per booking must be at least 1');
      return;
    }

    try {
      const equipmentData = {
        name: formData.name,
        description: formData.description,
        quantity: formData.quantity,
        active: formData.active,
        costAmount: formData.costAmount,
        costUnit: formData.costUnit,
        maxPerBooking: formData.maxPerBooking
      };

      console.log('Submitting equipment data:', equipmentData);

      if (isEditing && selectedEquipment) {
        await equipmentAPI.update(selectedEquipment.id, equipmentData);
        setSuccessMessage('Equipment updated successfully');
      } else {
        await equipmentAPI.create(equipmentData);
        setSuccessMessage('Equipment created successfully');
      }

      await fetchEquipment();
      setOpenCreateDialog(false);
    } catch (error: any) {
      console.error('Error saving equipment:', error);
      setError(isEditing ? 'Failed to update equipment' : 'Failed to create equipment');
    }
  };

  // Filter equipment based on search and filter criteria
  const filteredEquipment = useMemo(() => {
    let filtered = [...equipment];

    // Text search (name or description)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(eq =>
        eq.name.toLowerCase().includes(searchLower) ||
        (eq.description && eq.description.toLowerCase().includes(searchLower))
      );
    }

    // Active filter
    if (activeFilter === 'true') {
      filtered = filtered.filter(eq => eq.active === true);
    } else if (activeFilter === 'false') {
      filtered = filtered.filter(eq => eq.active === false);
    }

    return filtered;
  }, [equipment, searchText, activeFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setActiveFilter('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <AdminAccessControl allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.COACH]}>
        <Box sx={{ mt: 4, mb: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              color="inherit"
              startIcon={<HomeIcon />}
              sx={{ textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Typography color="text.primary">Equipment Management</Typography>
          </Breadcrumbs>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <BuildIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Equipment Management
            </Typography>
            <Stack direction="row" spacing={2}>
              <Tooltip title="Refresh Equipment List">
                <Button
                  variant="outlined"
                  onClick={fetchEquipment}
                  disabled={loading}
                >
                  <RefreshIcon />
                </Button>
              </Tooltip>
              <Tooltip title="Create new equipment">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateEquipment}
                >
                  Equipment
                </Button>
              </Tooltip>
            </Stack>
          </Box>

          {/* Success Snackbar */}
          <Snackbar
            open={!!successMessage}
            autoHideDuration={4000}
            onClose={() => setSuccessMessage(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccessMessage(null)} severity="success">
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon />
                <Typography variant="h6">Filters</Typography>
                {(searchText || activeFilter) && (
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    sx={{ ml: 'auto' }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>

              <Grid container spacing={2}>
                {/* Text Search */}
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Search"
                    placeholder="Search by name or description..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Active Filter */}
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Results count */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredEquipment.length} of {equipment.length} equipment items
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Equipment Grid */}
          <Grid container spacing={3}>
            {filteredEquipment.map((eq) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={eq.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography
                        variant="h6"
                        component="h2"
                        noWrap
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                        }}
                        onClick={() => handleEditEquipment(eq)}
                        title="Click to edit equipment"
                      >
                        {eq.name}
                      </Typography>
                      <Chip
                        label={eq.active ? 'Active' : 'Inactive'}
                        color={eq.active ? 'success' : 'default'}
                        size="small"
                        icon={eq.active ? <CheckCircleIcon /> : <CancelIcon />}
                      />
                    </Box>

                    <Stack spacing={1}>
                      {eq.description && (
                        <Typography variant="body2" color="text.secondary">
                          {eq.description}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InventoryIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Quantity: {eq.quantity}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          ${eq.costAmount} per {eq.costUnit} minutes
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Max per booking: {eq.maxPerBooking}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'center', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditEquipment(eq)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteEquipment(eq)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {equipment.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <BuildIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No equipment found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your first equipment item to get started
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateEquipment}>
                Create Equipment
              </Button>
            </Paper>
          )}

          {equipment.length > 0 && filteredEquipment.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <FilterListIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No equipment matches your filters
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Try adjusting your search criteria
              </Typography>
              <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Paper>
          )}

          {/* Create/Edit Dialog */}
          <Dialog
            open={openCreateDialog}
            onClose={(event, reason) => {
              if (reason === 'backdropClick') {
                return;
              }
              setOpenCreateDialog(false);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {isEditing ? 'Edit Equipment' : 'Create New Equipment'}
            </DialogTitle>
            <DialogContent>
              {/* Error Alert inside Dialog */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Stack spacing={3} sx={{ mt: 1 }}>
                {/* Equipment Name */}
                <TextField
                  label="Equipment Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  fullWidth
                  placeholder="e.g., Tennis Racket, Basketball"
                />

                {/* Description */}
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Optional description of the equipment"
                />

                {/* Quantity and Max Per Booking */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Total Quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    InputProps={{ inputProps: { min: 1 } }}
                    fullWidth
                    required
                    helperText="Total available quantity"
                  />

                  <TextField
                    label="Max Per Booking"
                    type="number"
                    value={formData.maxPerBooking}
                    onChange={(e) => setFormData({ ...formData, maxPerBooking: parseInt(e.target.value) || 1 })}
                    InputProps={{ inputProps: { min: 1 } }}
                    fullWidth
                    required
                    helperText="Max units per booking"
                  />
                </Box>

                {/* Cost Amount and Cost Unit */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Cost Amount"
                    type="number"
                    value={formData.costAmount}
                    onChange={(e) => setFormData({ ...formData, costAmount: parseFloat(e.target.value) || 0 })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputProps: { min: 0, step: 0.01 }
                    }}
                    fullWidth
                    required
                    helperText="Price per time unit"
                  />

                  <TextField
                    label="Cost Unit (minutes)"
                    type="number"
                    value={formData.costUnit}
                    onChange={(e) => setFormData({ ...formData, costUnit: parseInt(e.target.value) || 30 })}
                    InputProps={{ inputProps: { min: 1, step: 15 } }}
                    fullWidth
                    required
                    helperText="Duration in minutes"
                  />
                </Box>

                {/* Cost Preview */}
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Pricing:</strong> ${formData.costAmount} per {formData.costUnit} minutes
                    {formData.costAmount > 0 && formData.costUnit > 0 && (
                      <span>
                        {' '}(${(formData.costAmount / formData.costUnit * 60).toFixed(2)} per hour)
                      </span>
                    )}
                  </Typography>
                </Alert>

                {/* Active Toggle */}
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.active ? 'Active' : 'Inactive'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.active
                            ? 'Equipment is available for booking'
                            : 'Equipment cannot be booked'}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Box>
                {isEditing && (
                  <Button
                    onClick={() => {
                      setOpenCreateDialog(false);
                      setOpenDeleteDialog(true);
                    }}
                    color="error"
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained">
                  {isEditing ? 'Update' : 'Create'}
                </Button>
              </Box>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
            <DialogTitle>Delete Equipment</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete "{selectedEquipment?.name}"? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
              <Button onClick={confirmDeleteEquipment} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </AdminAccessControl>
    </Container>
  );
};

export default EquipmentManagementPage;
